import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Loader from '../components/Loader';
import Message from '../components/Message';
import MarkdownRenderer from '../components/MarkdownRenderer';
import MarkdownToolbar from '../components/MarkdownToolbar';
import ImageUploader from '../components/ImageUploader';

import {
  useCreateBlogPostMutation,
  useUpdateBlogPostMutation,
  useDeleteBlogPostMutation,
  useUploadBlogImageMutation,
} from '../slices/blogsApiSlice';

export default function BlogEditorScreen() {
  const { id: postId } = useParams();
  const navigate = useNavigate();
  const textareaRef = useRef();

  const isEditMode = Boolean(postId);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [tags, setTags] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [status, setStatus] = useState('draft');
  const [isRestricted, setIsRestricted] = useState(false);
  const [images, setImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [loaded, setLoaded] = useState(!isEditMode);

  const [createApi, { isLoading: isCreating }] = useCreateBlogPostMutation();
  const [updateApi, { isLoading: isUpdating }] = useUpdateBlogPostMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeleteBlogPostMutation();
  const [uploadImageApi] = useUploadBlogImageMutation();

  useEffect(() => {
    if (!isEditMode) return;

    const loadPost = async () => {
      try {
        const res = await fetch(`/api/me/blogs?pageSize=100`, {
          credentials: 'include',
        });
        const data = await res.json();
        const post = data.posts?.find((p) => p._id === postId);
        if (post) {
          setTitle(post.title);
          setContent(post.content || '');
          setExcerpt(post.excerpt || '');
          setTags(post.tags?.join(', ') || '');
          setIsPrivate(post.isPrivate || false);
          setStatus(post.status || 'draft');
          setIsRestricted(post.isRestricted || false);
          setImages(post.images?.map(img => ({
            _id: typeof img === 'string' ? img : img._id,
            filename: img.filename || 'image',
          })) || []);
          setFeaturedImage(post.featuredImage?._id || post.featuredImage || null);
        } else {
          toast.error('Post not found');
          navigate(`/me/blogs`);
        }
      } catch {
        toast.error('Failed to load post');
      } finally {
        setLoaded(true);
      }
    };
    loadPost();
  }, [isEditMode, postId, navigate]);

  const handleSave = async (targetStatus) => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!content.trim()) {
      toast.error('Content is required');
      return;
    }

    if (isEditMode && status === 'published' && targetStatus === 'draft') {
      if (!window.confirm('Unpublish this post and revert it to a draft? It will no longer be visible on the public blog.')) {
        return;
      }
    }

    const postData = {
      title: title.trim(),
      content,
      excerpt: excerpt.trim() || undefined,
      status: targetStatus,
      isPrivate,
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      featuredImage: featuredImage || null,
    };

    try {
      if (isEditMode) {
        await updateApi({ id: postId, ...postData }).unwrap();
        const prevStatus = status;
        setStatus(targetStatus);
        if (prevStatus === 'draft' && targetStatus === 'published') {
          toast.success('Post published!');
        } else if (prevStatus === 'published' && targetStatus === 'draft') {
          toast.success('Post reverted to draft');
        } else {
          toast.success('Post updated');
        }
      } else {
        const result = await createApi(postData).unwrap();
        toast.success(targetStatus === 'published' ? 'Post published!' : 'Draft saved');
        navigate(`/me/blogs/edit/${result._id}`);
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this blog post? This CANNOT be undone.')) return;
    try {
      await deleteApi(postId).unwrap();
      toast.success('Blog post deleted');
      navigate(`/me/blogs`);
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed');
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    let imageItem = null;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type && items[i].type.startsWith('image/')) {
        imageItem = items[i];
        break;
      }
    }

    if (!imageItem) return;

    e.preventDefault();

    if (!isEditMode) {
      toast.info('Save your post as a draft first, then you can upload images.');
      return;
    }

    const file = imageItem.getAsFile();
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image must be under 5 MB');
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, and GIF images are allowed');
      return;
    }

    const textarea = textareaRef.current;
    const start = textarea ? textarea.selectionStart : content.length;
    const end = textarea ? textarea.selectionEnd : content.length;
    const uploadToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const placeholder = `![Uploading ${file.name || 'image'}...]()<!-- upload-${uploadToken} -->`;

    const newContent = content.substring(0, start) + placeholder + content.substring(end);
    setContent(newContent);

    const formData = new FormData();
    formData.append('image', file);
    if (postId) formData.append('blogPostId', postId);

    try {
      const result = await uploadImageApi(formData).unwrap();
      const markdown = `![${result.filename || 'image'}](/api/blog/images/${result._id})`;
      setContent((prev) => prev.replace(placeholder, markdown));
      setImages((prev) => [...prev, result]);
      toast.success('Image uploaded and inserted');
    } catch (err) {
      setContent((prev) => prev.replace(placeholder, ''));
      toast.error(err?.data?.message || 'Image upload failed');
    }
  };

  if (!loaded) return <Loader />;

  return (
    <>
      <Link
        to={`/me/blogs`}
        className='btn btn-light'
        style={{ marginTop: '32px', marginBottom: '16px' }}
      >
        ← Back to My Posts
      </Link>

      <h1>{isEditMode ? 'Edit Blog Post' : 'New Blog Post'}</h1>

      {isRestricted && (
        <Message variant='danger'>
          <strong>Restricted by Administrator:</strong> This post has been restricted from public view. You can edit the content and save it as a draft, but it cannot be published until the restriction is lifted by an administrator.
        </Message>
      )}

      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group controlId='title' className='mb-3'>
          <Form.Label className='fw-bold'>Title</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter post title'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </Form.Group>

        <Form.Group controlId='content' className='mb-3'>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <Form.Label className='fw-bold mb-0'>Content (Markdown)</Form.Label>
            <Button
              size='sm'
              variant='outline-secondary'
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </div>
          <MarkdownToolbar textareaRef={textareaRef} />
          <Row>
            <Col md={showPreview ? 6 : 12}>
              <Form.Control
                as='textarea'
                ref={textareaRef}
                rows={18}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onPaste={handlePaste}
                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                placeholder='Write your blog post in Markdown...'
              />
            </Col>
            {showPreview && (
              <Col md={6}>
                <Card className='card--opaque' style={{ minHeight: '100%', overflow: 'auto' }}>
                  <Card.Body>
                    {content ? (
                      <MarkdownRenderer content={content} />
                    ) : (
                      <span className='text-muted'>Preview will appear here...</span>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </Form.Group>

        <Form.Group controlId='excerpt' className='mb-3'>
          <Form.Label className='fw-bold'>Excerpt (optional)</Form.Label>
          <Form.Control
            as='textarea'
            rows={2}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            maxLength={300}
            placeholder='Brief summary (auto-generated from content if left empty)'
          />
        </Form.Group>

        <Form.Group controlId='tags' className='mb-3'>
          <Form.Label className='fw-bold'>Tags (optional)</Form.Label>
          <Form.Control
            type='text'
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder='Comma-separated tags (e.g. ssd, benchmark, review)'
          />
          {tags && (
            <div className='mt-1'>
              {tags.split(',').map((t) => t.trim()).filter(Boolean).map((tag) => (
                <Badge key={tag} bg='secondary' className='me-1'>{tag}</Badge>
              ))}
            </div>
          )}
        </Form.Group>

        <Form.Group controlId='visibility' className='mb-3'>
          <Form.Check
            type='switch'
            id='visibility-switch'
            className='form-switch-success'
            label={!isPrivate ? 'Public — visible on the blog' : 'Private — only you can see this post'}
            checked={!isPrivate}
            onChange={(e) => setIsPrivate(!e.target.checked)}
          />
        </Form.Group>

        {isEditMode && (
          <ImageUploader
            blogPostId={postId}
            images={images}
            featuredImage={featuredImage}
            onImagesChange={setImages}
            onFeaturedChange={setFeaturedImage}
            textareaRef={textareaRef}
          />
        )}
        {!isEditMode && (
          <Message variant='info'>
            Save your post as a draft first, then you can upload images.
          </Message>
        )}

        <div className='d-flex gap-2 mt-3 mb-4'>
          {isEditMode && status === 'published' ? (
            <>
              <Button
                variant='primary'
                onClick={() => handleSave('published')}
                disabled={isCreating || isUpdating || isRestricted}
                title={isRestricted ? 'Restricted by administrator' : ''}
              >
                {isUpdating ? 'Updating…' : 'Update Post'}
              </Button>
              <Button
                variant='outline-secondary'
                onClick={() => handleSave('draft')}
                disabled={isCreating || isUpdating}
              >
                {isUpdating ? 'Saving…' : 'Switch to Draft'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant='secondary'
                onClick={() => handleSave('draft')}
                disabled={isCreating || isUpdating}
              >
                {isCreating || isUpdating ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button
                variant='primary'
                onClick={() => handleSave('published')}
                disabled={isCreating || isUpdating || isRestricted}
                title={isRestricted ? 'Restricted by administrator' : ''}
              >
                {isCreating || isUpdating ? 'Publishing…' : 'Publish'}
              </Button>
            </>
          )}
        </div>
      </Form>

      {isEditMode && (
        <Card className='border-danger mt-3 shadow-sm mb-5'>
          <Card.Header className='bg-danger text-white fw-bold'>
            Danger Zone
          </Card.Header>
          <Card.Body className='text-center p-4'>
            <p className='mb-4'>
              Warning: Deleting this blog post is permanent and CANNOT be undone.
            </p>
            <Button
              variant='danger'
              size='lg'
              className='w-100 fw-bold py-3'
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {isDeleting ? 'DELETING…' : 'DELETE'}
            </Button>
          </Card.Body>
        </Card>
      )}
    </>
  );
}
